/**
 * SettingWriteService.js
 * 
 * Service for writing setting data to Firestore.
 */

// Firebase
import { deleteDoc, doc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const SETTINGS_COLLECTION = collections.settings;

/**
 * Creates or replaces a settings document for a user.
 *
 * @param {string} userKey - The user key (document id).
 * @param {Object} data - Settings payload (UPPERCASE field names).
 *
 * @returns {Promise<Object|null>} The created settings data or null if inputs are invalid.
 */
export async function createSetting(userKey, data = {}) {

  // Validate inputs
  if (!userKey || !data) return null;

  // Timestamps
  const now = Timestamp.now();

  // Prepare payload
  const payload = {
    ...data,
    userKey,
    dateCreated: data?.dateCreated || now,
    dateModified: data?.dateModified || now,
  };

  // Create setting document
  await setDoc(doc(db, SETTINGS_COLLECTION, userKey), payload, { merge: false });

  // Return the created setting payload
  return payload;
}

/**
 * Updates an existing settings document for a user.
 *
 * @param {string} userKey - The user key (document id).
 * @param {Object} data - The settings fields to update (UPPERCASE field names).
 *
 * @returns {Promise<void>}
 */
export async function updateSetting(userKey, data) {

  // Validate inputs
  if (!userKey || !data) return;

  // Prevent userKey changes
  if (data?.userKey && data.userKey !== userKey) return;

  // Prepare payload with updated dateModified
  const payload = {
    ...data,
    dateModified: data?.dateModified || Timestamp.now(),
  };

  // Update setting document
  await updateDoc(doc(db, SETTINGS_COLLECTION, userKey), payload);
}

/**
 * Deletes a settings document by user key.
 *
 * @param {string} userKey - The user key (document id) to delete.
 *
 * @returns {Promise<void>} - Promise that resolves when the settings document is deleted.
 */
export async function deleteSettingByKey(userKey) {

  // Validate input
  if (!userKey) return;

  // Delete setting document
  await deleteDoc(doc(db, SETTINGS_COLLECTION, userKey));
}
